import { useMemo } from 'react';
import { ListItemButton } from '@mui/material';
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { LegStatus } from '@api/Trinium/types';
import DisabledTooltip from '@components/DisabledTooltip';
import { queueModal } from '@components/Modals/modalSlice';
import { useAppDispatch } from '@reduxStore';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';

/**
 * View the assigned driver's position against the leg's pickup to delivery route
 */
export default function ViewDriverRouteAction() {
	const gridApiRef = useGridApiContext();
	const appDispatch = useAppDispatch();
	const selectedRowIds = useGridSelector(gridApiRef, gridRowSelectionIdsSelector);

	const leg = useMemo(() => {
		const legId = selectedRowIds.keys().next().value;
		return legId ? gridApiRef.current.getRow<DispatchLeg>(legId) : undefined;
	}, [gridApiRef, selectedRowIds]);

	// This action requires exactly 1 row to be selected
	if (selectedRowIds.size !== 1 || !leg) {
		return <></>;
	}

	// Enabled only for a leg with a driver assigned that is Available or Picked Up
	const legStatus = getEnumValueByKey(LegStatus, leg.legStatus) as LegStatus;
	let disabledMessage: string | undefined;
	if (!leg.drvId) {
		disabledMessage = 'Driver not currently assigned';
	} else if (![LegStatus.AV, LegStatus.PU].includes(legStatus)) {
		disabledMessage = 'Load must be Available or Picked Up';
	}

	const handleBtnClick = () => {
		appDispatch(
			queueModal({
				Component: 'viewDriverRouteModal',
				Props: {
					legId: leg.legId
				}
			})
		);
	};

	return (
		<DisabledTooltip message={disabledMessage}>
			<ListItemButton
				onClick={handleBtnClick}
				disabled={Boolean(disabledMessage)}
			>
				View Driver Route
			</ListItemButton>
		</DisabledTooltip>
	);
}
